<template>
  <Button severity="primary" @click="visible = true">
    <IconPlus />
    Add Metric
  </Button>

  <DialogFrame
    v-model:visible="visible"
    :loading="loading"
    class="p-dialog-ultra-wide modal--widgets"
    @onHide="onCloseModal"
    @onClose="onCloseModal"
  >
    <template #title>Add Metrics</template>

    <template #body>
      <div
        v-for="category in widgetsByCategory"
        :index="category.category"
        :key="category.category"
      >
        <h3>
          {{ category.category }}
          <IconPremium v-if="category.category === 'Premium'" class="premium-category-icon" />
        </h3>
        <ul class="widget-list">
          <li
            v-for="widget in category.widgets"
            :key="widget.id"
            :class="{ 'widget-in-use': widgetIds?.includes(widget.id) }"
          >
            <div class="widget-header">
              <div class="widget-title">
                <span class="icon-analytics"><IconAnalytics /></span>
                {{ widget.name }}
              </div>
              <div v-if="widget.isBenchmarking" class="widget-benchmarking">
                <IconCompare />
                <span>Benchmark Available</span>
              </div>
              <div v-if="widgetIds?.includes(widget.id)" class="widget-used">In Use!</div>
              <div v-else>
                <Button severity="secondary" @click="clickedAddMetric(widget)">
                  <IconPlus />
                </Button>
              </div>
            </div>
            <div class="widget-description">{{ widget.description }}</div>
          </li>
        </ul>
      </div>
    </template>

    <template #footer>
      <Button :disabled="loading" label="Done" severity="primary" @click="saveModal()" autofocus />
    </template>
  </DialogFrame>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';

import { getWidgetById } from '@/utils/api';
import type { WidgetObjectSorted } from '@/types/Widget';

import { Button } from 'primevue';

import IconAnalytics from '@/icons/IconAnalytics.svg?component';
import IconPlus from '@/icons/IconPlus.svg?component';
import IconCompare from '@/icons/analytics/IconCompare.svg?component';
import IconPremium from '@/icons/IconPremium.svg?component';

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useAnalyticsStore } from '@/stores/analytics';
import DialogFrame from './DialogFrame.vue';
const analyticsStore = useAnalyticsStore();

const loading = ref(false);

const widgets = defineModel<WidgetObjectSorted[]>('widgets');

const widgetIds = computed(() => {
  return widgets.value?.map((widget) => widget.id);
});

type WidgetPreview = {
  id: number;
  name: string;
  description: string;
  isBenchmarking?: boolean;
  default_width?: number;
  default_height?: number;
};

type CategoryWidgets = {
  category: string;
  widgets: WidgetPreview[];
};

const widgetsByCategory: CategoryWidgets[] = [
  ...(userStore.hasFeature('premium_metrics_are_ready_for_production')
    ? [
        {
          category: 'Premium',
          widgets: [
            {
              id: 17,
              name: 'Cumulative Arrival',
              description: 'Percentage of visitors arriving on site over time.',
            },
            {
              id: 18,
              name: 'Arrivals by Day',
              description: 'Count of when visitors arrived on site.',
              default_width: 2,
              default_height: 2,
            },
            {
              id: 20,
              name: 'Most Common Path',
              description: 'Most popular zone visit sequences across the event.',
              default_width: 2,
              default_height: 2,
            },
            {
              id: 25,
              name: 'Zone-to-Zone Crossover',
              description:
                'Visitor movement between zones, as a percentage of visitors to that zone. Ordered Overlap only counts visitors that were later seen at the second zone.',
              default_width: 2,
              default_height: 2,
            },
            {
              id: 26,
              name: 'Total Engaged Dwell Time by Zone',
              description: 'Distribution of engaged time spent by visitors across zones.',
              default_width: 2,
              default_height: 2,
            },
          ],
        },
      ]
    : []),
  ...(userStore.hasFeature('basic_benchmarking_widgets')
    ? [
        {
          category: 'Benchmarking',
          widgets: [
            {
              id: 15,
              name: 'Zone Engaged Repeat Visits',
              description: 'Engaged visits beyond the first engaged visit, per zone.',
              isBenchmarking: true,
            },
            {
              id: 13,
              name: 'Zone Conversion Rate',
              description:
                'The percentage of Opportunity To See (OTS) that became engaged, per zone.',
              isBenchmarking: true,
            },
            {
              id: 14,
              name: 'Zone Engaged Dwell Time',
              description: 'Average visit time for engaged visits, per zone.',
              isBenchmarking: true,
            },
          ],
        },
      ]
    : []),
  {
    category: 'Performance',
    widgets: [
      {
        id: 3,
        name: 'Unique Visitors',
        description:
          'Unique Visitors: Total distinct detected visitors. Conversion Rate: Percentage of visitors that became engaged.',
        default_width: 2,
        default_height: 2,
      },
      {
        id: 1,
        name: 'Engaged Visit Dwell Time',
        description:
          'Average time spent within zones per individual visit. Separate visits by the same visitor are treated individually.',
      },
      {
        id: 2,
        name: 'Engaged Visitor Dwell Time',
        description:
          'Average total time spent within zones per visitor. Separate visits by the same visitor are summed.',
      },
      {
        id: 4,
        name: 'Average Engaged Visits',
        description: 'Average number of times seen within zones per visitor.',
        default_width: 1,
        default_height: 1,
      },
      {
        id: 5,
        name: 'Average Repeats',
        description:
          'Average number of visits beyond the first by a visitor. Three visits in the same day by a visitor will count as two repeated visits beyond the first.',
        default_width: 1,
        default_height: 1,
      },
    ],
  },
  {
    category: 'Activations',
    widgets: [
      {
        id: 6,
        name: 'Zones Visited',
        description: 'The division of total engaged visits across all zones.',
        default_width: 2,
        default_height: 2,
      },
      {
        id: 12,
        name: 'Days Visited',
        description: 'Number of days a visitor was seen on.',
      },
    ],
  },
  {
    category: 'Engagement',
    widgets: [
      {
        id: 11,
        name: 'Engaged Visit Distributed Dwell',
        description: 'Percentage of visits that lasted each duration in minutes.',
      },
      {
        id: 8,
        name: 'Engaged Visits',
        description: 'Detections of engaged visitors.',
      },
      {
        id: 10,
        name: 'Repeat Visitors',
        description:
          'Total visitors detected multiple times either within the same day or between days.',
      },
      {
        id: 9,
        name: 'Repeat Visits',
        description: 'Total visits that are not the first visit made by each visitor.',
      },
    ],
  },
];

const emit = defineEmits(['onAddedWidget']);
const visible = ref(false);

const clickedAddMetric = (widget: WidgetPreview) => {
  const eventId = analyticsStore.chosenEvent?.id;

  if (eventId) {
    getWidgetById(userStore.accessToken, eventId, widget.id).then((resp) => {
      const maxY = Math.max(...analyticsStore.chosenWidgets.map((widget) => widget.y));
      const lastWidget = analyticsStore.chosenWidgets.find((widget) => widget.y === maxY);
      let newY = maxY;

      if (lastWidget?.h) {
        newY = maxY + lastWidget.h;
      }

      const widgetData = resp;
      widgetData.position = {
        id: widget.id,
        i: analyticsStore.chosenWidgets.length + 1,
        w: widget.default_width ?? 1,
        h: widget.default_height ?? 2,
        x: 0,
        y: newY,
      };

      const newWidget = {
        id: widget.id,
        i: analyticsStore.chosenWidgets.length + 1,
        w: widget.default_width ?? 1,
        h: widget.default_height ?? 2,
        x: 0,
        y: newY,
        widget: resp,
      };

      analyticsStore.chosenWidgets.push(newWidget);
      nextTick(() => {
        emit('onAddedWidget', widget.id);
      });
    });
  }
};

const saveModal = () => {
  visible.value = false;
};

const onCloseModal = () => {
  visible.value = false;
};
</script>

<style lang="scss">
@use 'sass:color';

.modal--widgets {
  h3 {
    @include typography.font-body($size: sm, $weight: medium);
    align-items: center;
    color: variables.$vantage-base-primitives-color-red-100;
    display: flex;
    gap: variables.$vantage-base-sizing-xxs;
    letter-spacing: 1px;
    margin-bottom: variables.$vantage-base-sizing-md;
    text-transform: uppercase;

    .premium-category-icon {
      margin-left: variables.$vantage-base-sizing-xxxs;
    }
  }

  .widget-list {
    @include mixins.flex-center(
      $horiz: flex-start,
      $gap: variables.$vantage-base-sizing-sm,
      $wrap: true
    );
    align-items: stretch;
    list-style-type: none;
    margin-bottom: variables.$vantage-base-sizing-xxxl;

    li {
      @include mixins.rounded-border(
        $color: variables.$vantage-base-primitives-color-background-300
      );
      @include sizing.padding();
      background: variables.$vantage-base-primitives-color-background-300;
      flex-basis: calc(50% - 8px);
      flex-grow: 0;
      flex-shrink: 0;
      position: relative;
      transition:
        background-color 0.3s ease-in-out,
        border-color 0.3s ease-in-out;
      will-change: background-color, border-color;
    }

    .widget-header {
      @include mixins.flex-center($horiz: space-between, $gap: variables.$vantage-base-sizing-xs);
      margin-bottom: variables.$vantage-base-sizing-xxs;
    }

    .widget-title {
      @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xs);
      @include typography.font-body($size: md, $weight: medium);
      color: variables.$vantage-base-primitives-color-tertiary-500;
    }

    .icon-analytics {
      @include mixins.flex-center($horiz: center);
      @include mixins.rounded-border($size: 6px);
      align-items: center;
      background: variables.$vantage-base-primitives-color-background-100;
      height: 26px;
      width: 26px;
    }

    .widget-in-use {
      .widget-title,
      .widget-description {
        opacity: 0.5;
      }
    }

    .widget-used {
      @include sizing.padding('xxs');
      @include typography.font-body($size: sm, $weight: normal);
      background: variables.$vantage-base-primitives-color-tertiary-500;
      border-radius: variables.$vantage-base-sizing-xxxs;
      color: variables.$vantage-base-primitives-color-background-100;
      margin-right: -24px;
      white-space: nowrap;
    }

    .widget-benchmarking {
      @include mixins.flex-center($horiz: flex-start, $gap: variables.$vantage-base-sizing-xxxs);
      @include typography.font-body($size: xs, $weight: medium);
      @include mixins.rounded-border(
        $color: variables.$vantage-base-primitives-color-background-300,
        $size: 9999px
      );
      @include sizing.padding('xxxs', 'xs');
      white-space: nowrap;
      color: variables.$vantage-base-primitives-color-primary-400;
      background: variables.$vantage-base-primitives-color-background-300;
    }

    li:not(.widget-in-use):hover {
      background-color: variables.$vantage-base-primitives-color-border-200;
      border-color: variables.$vantage-base-primitives-color-blue-300;

      .p-button {
        opacity: 1;
      }
    }

    .p-button {
      @include sizing.padding('xxxs');
      opacity: 0;
      transition: opacity 0.3s ease-in-out;
      will-change: opacity;
    }
  }

  .p-button svg {
    margin: 0;
  }
}
</style>
